import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Stack,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  IconButton,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Divider,
  Box,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowDown } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import * as XLSX from "xlsx/xlsx.mjs";

import { DataContext } from "../../../context/DataContext";
import { getRol, getToken, getUser } from "../../../api/Account";
import { getCumplimientoVisitasAsync } from "../../../api/Ruta";
import { simpleMessage } from "../../../helpers/Helpers";
import { EncabezadoOrdenable, useOrdenTabla } from "../../../helpers/OrdenTabla";
import NoData from "../../../components/NoData";
import { PrintReport } from "../../../components/PrintReport";

const moneda = (valor) =>
  new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(
    valor
  );

const VERDE = "#00a152";
const NARANJA = "#ed6c02";
const ROJO = "#d32f2f";

/**
 * Qué se sabe de cada cliente del día. El caso que importa es el último: no pagó y el
 * gestor nunca estuvo cerca. Los demás existen para poder descartarlos.
 */
const situacionDe = (v) => {
  if (v.pago) {
    return { texto: "Cobrado", color: VERDE };
  }
  if (v.sinUbicacion) {
    return { texto: "Sin ubicación", color: "#757575" };
  }
  if (v.pasoCerca) {
    return { texto: "Visitado, no pagó", color: NARANJA };
  }
  return { texto: "No visitado", color: ROJO };
};

const CumplimientoVisitas = () => {
  const { selectedGestor, selectedFecha } = useParams();
  const [reporte, setReporte] = useState(null);

  const compRef = useRef();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const { orden, cambiarOrden, ordenar } = useOrdenTabla("fullName", [
    "fullName",
    "address",
    "phone",
    "tipo",
  ]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getCumplimientoVisitasAsync(
        token,
        selectedGestor,
        selectedFecha
      );
      setIsLoading(false);

      if (!result.statusResponse) {
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setReporte(result.data.result);
    })();
  }, []);

  const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page { size: landscape;  margin: 4mm; }
`;

  if (reporte === null) {
    return null;
  }

  const visitas = reporte.visitas ?? [];
  const filas = ordenar(visitas);

  const cobrados = visitas.filter((v) => v.pago).length;
  const visitadosSinPagar = visitas.filter(
    (v) => !v.pago && v.pasoCerca
  ).length;
  const noVisitados = visitas.filter(
    (v) => !v.pago && !v.pasoCerca && !v.sinUbicacion
  ).length;
  const sinUbicacion = visitas.filter((v) => !v.pago && v.sinUbicacion).length;

  const montoCobrado = visitas.reduce((t, v) => t + v.montoPagado, 0);
  const montoNoVisitado = visitas
    .filter((v) => !v.pago && !v.pasoCerca && !v.sinUbicacion)
    .reduce((t, v) => t + v.cuota + v.atraso, 0);

  const exportExcel = () => {
    let exp = [];

    filas.forEach((v) => {
      exp.push({
        cliente: v.fullName,
        direccion: v.address,
        telefono: v.phone,
        tipo: v.tipo,
        cuota: v.cuota,
        atraso: v.atraso,
        atrasoDias: v.atrasoDias,
        saldoTotal: v.saldoTotal,
        pago: v.pago ? "SI" : "NO",
        montoPagado: v.montoPagado,
        situacion: situacionDe(v).texto,
        metrosMasCerca: v.metrosMasCerca ?? "",
        horaMasCerca: v.horaMasCerca ? moment(v.horaMasCerca).format("hh:mm A") : "",
        gestor: reporte.gestorName,
      });
    });

    var bk = XLSX.utils.book_new(),
      bs = XLSX.utils.json_to_sheet(exp);

    XLSX.utils.book_append_sheet(bk, bs, "cumplimientoVisitas");
    XLSX.writeFile(bk, "CumplimientoVisitas.xlsx");
  };

  const t = (compacto, chico, grande) => (compacto ? chico : grande);

  const parametros = (compacto) => (
    <Stack direction={"row"} justifyContent="space-between">
      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Parametros:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Gestor Seleccionado: ${reporte.gestorName}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Fecha: ${moment(reporte.fecha).format("L")}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {reporte.puntosDeRecorrido === 0
            ? "Recorrido: sin puntos registrados ese día"
            : `Recorrido: ${reporte.puntosDeRecorrido} puntos, de ${moment(
                reporte.primerPunto
              ).format("hh:mm A")} a ${moment(reporte.ultimoPunto).format("hh:mm A")}`}
        </Typography>
      </Stack>

      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Usuario:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {user}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {rol}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Fecha y Hora: ${moment().format("DD/MM/YYYY hh:mm A")}`}
        </Typography>
      </Stack>
    </Stack>
  );

  const totales = (compacto) => {
    const tamano = t(compacto, 11, 14);

    const Total = ({ titulo, valor, color }) => (
      <Stack textAlign="center">
        <Typography
          style={{ fontWeight: "bold", color: color ?? "#03a9f4", fontSize: tamano }}
        >
          {titulo}
        </Typography>
        <Typography style={{ fontSize: tamano }}>{valor}</Typography>
      </Stack>
    );

    return (
      <>
        <Stack direction="row" flex="row" justifyContent="space-around">
          <Total titulo="Clientes del Día" valor={visitas.length} />
          <Total titulo="Cobrados" valor={cobrados} color={VERDE} />
          <Total titulo="Visitados, no pagó" valor={visitadosSinPagar} color={NARANJA} />
          <Total titulo="No visitados" valor={noVisitados} color={ROJO} />
          <Total titulo="Sin ubicación" valor={sinUbicacion} />
        </Stack>

        <Divider sx={{ marginY: "10px" }} />

        <Stack direction="row" flex="row" justifyContent="space-around">
          <Total titulo="Total Cobrado" valor={moneda(montoCobrado)} color={VERDE} />
          {/* Lo que quedó sin cobrar de gente a la que además nadie fue a ver. Es la
          cifra que da sentido al reporte. */}
          <Total
            titulo="Dejado de Cobrar sin Visitar"
            valor={moneda(montoNoVisitado)}
            color={ROJO}
          />
          <Total
            titulo="Radio de Cercanía"
            valor={`${reporte.radioMetros} metros`}
          />
        </Stack>
      </>
    );
  };

  const tabla = (compacto) => {
    const th = { fontSize: t(compacto, 9, 14) };
    const td = { fontSize: t(compacto, 8, 13) };

    return (
      <TableContainer>
        <Table aria-label="simple table" size="small">
          <TableHead>
            <TableRow>
              <EncabezadoOrdenable campo="fullName" orden={orden} cambiarOrden={cambiarOrden} align="left" className="text-primary fw-bold" style={th}>
                Cliente
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="address" orden={orden} cambiarOrden={cambiarOrden} align="left" className="text-primary fw-bold" style={th}>
                Dirección
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="phone" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Teléfono
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="tipo" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Tipo
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="cuota" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Cuota
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="atraso" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Atraso
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="saldoTotal" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Saldo Total
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="montoPagado" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Pagado
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="metrosMasCerca" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Más Cerca
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="horaMasCerca" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Hora
              </EncabezadoOrdenable>
              <EncabezadoOrdenable campo="pasoCerca" orden={orden} cambiarOrden={cambiarOrden} align="center" className="text-primary fw-bold" style={th}>
                Situación
              </EncabezadoOrdenable>
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((v) => {
              const situacion = situacionDe(v);
              return (
                <TableRow key={v.creditId}>
                  <TableCell align="left" component="th" scope="row" style={td}>
                    {v.fullName}
                  </TableCell>
                  <TableCell align="left" component="th" scope="row" style={td}>
                    {v.address}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {v.phone}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {v.tipo}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {moneda(v.cuota)}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {moneda(v.atraso)}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {moneda(v.saldoTotal)}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {v.pago ? moneda(v.montoPagado) : ""}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {v.metrosMasCerca === null ? "" : `${v.metrosMasCerca} m`}
                  </TableCell>
                  <TableCell align="center" scope="row" style={td}>
                    {v.horaMasCerca ? moment(v.horaMasCerca).format("hh:mm A") : ""}
                  </TableCell>
                  <TableCell
                    align="center"
                    scope="row"
                    style={{
                      ...td,
                      fontWeight: "bold",
                      color: situacion.color,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {situacion.texto}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /**
   * Qué prueba y qué no prueba este reporte. Va en la hoja porque sobre estos números se
   * le llama la atención a una persona, y quien los lea tiene que saber de dónde salen.
   */
  const leyenda = (compacto) => {
    const chico = t(compacto, 8, 12);

    return (
      <>
        <Divider sx={{ marginY: "10px" }} />
        <Typography
          fontSize={t(compacto, 9, 13)}
          fontWeight="bold"
          textAlign="left"
          sx={{ color: "text.secondary" }}
        >
          Cómo se lee este reporte
        </Typography>

        <Stack marginTop={0.5}>
          <Typography fontSize={chico} textAlign="left">
            <span style={{ color: VERDE, fontWeight: "bold" }}>Cobrado: </span>
            se le registró un abono ese día.
          </Typography>
          <Typography fontSize={chico} textAlign="left">
            <span style={{ color: NARANJA, fontWeight: "bold" }}>Visitado, no pagó: </span>
            {`el recorrido del gestor pasó a menos de ${reporte.radioMetros} metros de la ubicación del cliente, pero no hubo abono.`}
          </Typography>
          <Typography fontSize={chico} textAlign="left">
            <span style={{ color: ROJO, fontWeight: "bold" }}>No visitado: </span>
            {`el gestor nunca estuvo a menos de ${reporte.radioMetros} metros en todo el día.`}
          </Typography>
          <Typography fontSize={chico} textAlign="left">
            <span style={{ fontWeight: "bold" }}>Sin ubicación: </span>
            no se le conoce la casa todavía, así que no se puede afirmar nada. No cuenta
            como no visitado.
          </Typography>

          <Typography fontSize={chico} textAlign="left" marginTop={0.5}>
            <span style={{ fontWeight: "bold" }}>Qué prueba y qué no. </span>
            {`El recorrido demuestra que el gestor estuvo cerca, no que haya tocado la puerta: puede haber pasado de largo camino a otro cliente. Al revés es más firme: si en todo el día nunca estuvo a menos de ${reporte.radioMetros} metros, no lo visitó. Por eso el radio es amplio — es preferible dejar pasar un caso que acusar a alguien que sí fue.`}
          </Typography>

          <Typography fontSize={chico} textAlign="left" marginTop={0.5}>
            <span style={{ fontWeight: "bold" }}>La ubicación del cliente </span>
            sale de dónde se le ha cobrado más veces, o de la que alguien fijó a mano desde
            la aplicación. Un cliente recién ubicado o con un punto malo puede aparecer como
            no visitado sin serlo.
          </Typography>
        </Stack>
      </>
    );
  };

  return (
    <div>
      <Dialog fullScreen open={true}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
            <Typography
              sx={{ ml: 2, flex: 1, textAlign: "center" }}
              variant="h5"
              component="div"
            >
              {`${title} `}
            </Typography>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
          </Toolbar>
        </AppBar>

        <Stack display="flex" justifyContent="center">
          <Stack
            direction={"row"}
            justifyContent="space-between"
            paddingLeft={5}
            paddingRight={5}
            marginTop={1}
            marginBottom={1}
          >
            <IconButton
              onClick={() => exportExcel()}
              sx={{ color: "primary.main" }}
            >
              <FontAwesomeIcon
                icon={faCloudArrowDown}
                style={{ fontSize: 22, width: 22 }}
              />
            </IconButton>

            <Stack>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {slogan}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
                variant="h6"
                component="div"
              >
                Reporte de Cumplimiento de Visitas
              </Typography>
            </Stack>

            <ReactToPrint
              trigger={() => {
                return (
                  <IconButton sx={{ color: "info.main" }}>
                    <PrintRoundedIcon style={{ fontSize: 22, width: 22 }} />
                  </IconButton>
                );
              }}
              content={() => compRef.current}
              pageStyle={pageStyle}
            />
          </Stack>

          <Box sx={{ paddingX: "22px", textAlign: "center" }}>
            {parametros(false)}
          </Box>
        </Stack>

        <Box sx={{ paddingX: "22px", textAlign: "center" }}>
          <Divider sx={{ marginY: "14px" }} />

          {isEmpty(visitas) ? (
            <NoData />
          ) : (
            <>
              {reporte.puntosDeRecorrido === 0 ? (
                <Typography
                  fontSize={17}
                  color={NARANJA}
                  marginY={2}
                  sx={{ maxWidth: 760, marginX: "auto", lineHeight: 1.5 }}
                >
                  Ese día no se registró ningún punto de recorrido de este gestor, así que
                  no hay con qué comparar: todos aparecen como no visitados sin que eso
                  signifique nada. Revisá que el seguimiento esté activo en su teléfono.
                </Typography>
              ) : null}

              {totales(false)}
              <Divider sx={{ marginY: "14px" }} />
              {tabla(false)}
              {leyenda(false)}
            </>
          )}
        </Box>
      </Dialog>

      <div style={{ display: "none" }}>
        <PrintReport ref={compRef} titulo={"Reporte de Cumplimiento de Visitas"}>
          <Container maxWidth={false} sx={{ textAlign: "center" }}>
            {parametros(true)}
            <Divider sx={{ marginY: "10px" }} />

            {totales(true)}
            <Divider sx={{ marginY: "10px" }} />

            {tabla(true)}
            {leyenda(true)}
          </Container>
        </PrintReport>
      </div>
    </div>
  );
};

export default CumplimientoVisitas;
